import type { ReactNode } from "react";

export interface TabDef {
  /** Stable key used for selection and as the rendered button id. */
  key: string;
  label: ReactNode;
}

export interface TabsProps {
  tabs: TabDef[];
  /** The currently-selected tab key (controlled). */
  activeKey: string;
  onChange: (key: string) => void;
  /** Accessible name for the tablist. */
  ariaLabel?: string;
}

/**
 * A horizontal tab strip — the `fui-tabs` vocabulary. Mirrors the Jmix `JmixTabSheet` look used on
 * the animal detail (muted labels, a thin full-width rule, a primary underline under the active tab).
 * Controlled: the host owns the active key and the rendered tab panels. Each button carries
 * `id="fui-tab-<key>"` so a panel can reference it via `aria-labelledby`.
 */
export function Tabs({ tabs, activeKey, onChange, ariaLabel }: TabsProps) {
  return (
    <div className="fui-tabs" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          id={`fui-tab-${tab.key}`}
          className="fui-tab"
          aria-selected={activeKey === tab.key}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
